import { Box, Card, Text } from '@shopify/polaris';
import { useMemo, useState } from 'react';
import { VerticalSpace } from '../../../UI/Reports/VerticalSpace';
import { useCurrency } from '../../../../customHooks/useCurrency';
import { BreakdownByCategory } from './BreakdownByCategory';
import { RadarChart } from './RadarChart';
import { aggregateByDimensions } from './aggregateByDimensions';
import { formatMoney, formatPct, formatUnits } from './formatters';

// Paleta para los arcos / labels de agrupamiento del radar. Dos grises
// que alternan para separar grupos adyacentes sin meter color al
// gráfico. Ambos lo bastante oscuros para que los títulos sigan
// siendo legibles sobre fondo blanco.
const GROUP_PALETTE = ['#374151', '#9CA3AF'];

// Etiquetas legibles para cada dimensión — se usan en el título
// dinámico de la Card de métricas por categoría.
const DIM_LABEL = { gender: 'Género', category: 'Categoría', subcategory: 'Subcategoría' };

/**
 * Tab Análisis: agrupa visualmente los datos del informe por dimensiones
 * (género · categoría · subcategoría). Tres bloques con toggles
 * independientes:
 *   - Radar de valores (Compra / Venta PVF / Almacén)
 *   - Radar de unidades (Compradas / Vendidas / Restantes)
 *   - Tabla breakdown (con botones para añadir/quitar categoría y
 *     subcategoría a la agrupación)
 */
export const AnalysisView = ({ rows, genderFilterActive, showSub = false, campaignLabel = '' }) => {
  const { currency } = useCurrency();
  // Modo del chart unificado: valores económicos o unidades.
  const [chartMode, setChartMode] = useState('value');
  // `showSub` viene de fuera — vive en ViewReportEfficacy para que el
  // mismo toggle afecte también al radar de almacén del Resumen.

  // Dimensiones del agrupamiento (orden importa: gender → category →
  // subcategory). Si hay filtro de género activo no añadimos la
  // dimensión gender — sería siempre el mismo valor. Categoría va
  // siempre; subcategoría sólo si el usuario la activa.
  const dimensions = useMemo(
    () => [!genderFilterActive && 'gender', 'category', showSub && 'subcategory'].filter(Boolean),
    [genderFilterActive, showSub],
  );

  // Para los charts: usamos las MISMAS dimensiones que la tabla. Si no
  // hay ninguna activa, fallback a 'category' para que el radar tenga
  // algún grupo que pintar.
  const chartDimensions = useMemo(() => (dimensions.length > 0 ? dimensions : ['category']), [dimensions]);
  const chartGroups = useMemo(() => aggregateByDimensions(rows, chartDimensions), [rows, chartDimensions]);

  // Orden lexicográfico por (género · categoría · subcategoría). Así
  // los ejes del radar quedan agrupados visualmente: todos los ejes
  // de un mismo género/categoría salen contiguos y se pueden marcar
  // con un arco coloreado por fuera (ver `axisGroups`). Sin cap.
  const compareGroups = (a, b) => {
    for (const dim of ['gender', 'category', 'subcategory']) {
      const av = String(a[dim] ?? '');
      const bv = String(b[dim] ?? '');
      const cmp = av.localeCompare(bv, 'es', { sensitivity: 'base' });
      if (cmp !== 0) return cmp;
    }
    return 0;
  };
  const topForChart = useMemo(() => [...chartGroups].sort(compareGroups), [chartGroups]);

  // Agrupamiento visual del radar. La dimensión "coarse" para agrupar
  // es la más alta de la jerarquía disponible:
  //   · si hay género en las dims → agrupamos por género
  //   · si hay subcategoría (con categoría) → agrupamos por categoría
  //   · si no, no hay agrupamiento (todo plano)
  // Cada eje recibe { key, color }; ejes contiguos con misma key forman
  // un "run" y se pintan con un arco exterior + etiqueta del grupo.
  const groupingDim = chartDimensions.includes('gender')
    ? 'gender'
    : chartDimensions.includes('subcategory')
      ? 'category'
      : null;
  const axisGroups = useMemo(() => {
    if (!groupingDim) return null;
    const colorByKey = new Map();
    return topForChart.map((g) => {
      const key = String(g[groupingDim] ?? '—');
      if (!colorByKey.has(key)) {
        colorByKey.set(key, GROUP_PALETTE[colorByKey.size % GROUP_PALETTE.length]);
      }
      return { key, color: colorByKey.get(key) };
    });
  }, [topForChart, groupingDim]);

  const pct = (n) => formatPct(n);

  // Picos (ejes) — un objeto por grupo. Etiqueta en 2 líneas:
  //   primary = subcategoría si la hay, si no categoría
  //   secondary = género si está activo (siempre que no sea primary)
  // El género se muestra debajo en gris para no confundir con la dim
  // principal del pico. El placeholder "—" que produce el agregador
  // para valores vacíos se trata como "ausente" — no es informativo
  // mostrarlo como subcat, mejor hacer fallback a categoría.
  const isEmptyDim = (v) => v == null || v === '—';
  const axisOf = (g) => {
    const primary = !isEmptyDim(g.subcategory)
      ? g.subcategory
      : !isEmptyDim(g.category)
        ? g.category
        : !isEmptyDim(g.gender)
          ? g.gender
          : '—';
    const showGender = chartDimensions.includes('gender') && !isEmptyDim(g.gender) && primary !== g.gender;
    return { primary, secondary: showGender ? g.gender : null };
  };
  const axes = topForChart.map(axisOf);

  // Ratios. División segura (devuelve 0 si denom <= 0).
  const safeRatio = (num, den) => (den > 0 ? num / den : 0);

  // Totales agregados sobre TODO el informe (no sólo el top 12 del
  // radar). El ratio Σ num / Σ den es la media ponderada — más
  // significativa que sumar ratios. Se muestran en la leyenda como
  // "Totales" cuando no hay hover sobre un eje.
  const sum = (arr, k) => (arr || []).reduce((acc, g) => acc + (Number(g[k]) || 0), 0);
  const totalBuyValue = sum(rows, 'buyValue');
  const totalPvfIn = sum(rows, 'sellValuePvfInSeason');
  const totalPvfTotal = sum(rows, 'sellValuePvfTotal');
  const totalRemainCI = sum(rows, 'remainValueCI');
  const totalBuyUnits = sum(rows, 'buyUnits');
  const totalSellUnitsIn = sum(rows, 'sellUnitsInSeason');
  const totalSellUnitsTotal = sum(rows, 'sellUnitsTotal');
  const totalRemainUnits = sum(rows, 'remainUnits');

  const fmt$ = (n) => formatMoney(n, currency);
  const fmtU = (n) => `${formatUnits(n)} uds`;

  // VALORES: tres series como % de la Compra de cada categoría.
  // - Venta campaña / Compra
  // - Venta total   / Compra
  // - Almacén       / Compra
  // El "Compra" de cada grupo siempre es 100% — por eso no se pinta
  // como serie. Los polígonos cuentan qué % de la compra se ha
  // recuperado en cada tramo. `rawValues`/`rawTotal`/`formatRaw` se
  // pasan para que la leyenda muestre la magnitud absoluta junto al %.
  const valueSeries = [
    // "Entregado" = la compra de cada categoría. Es el 100% que sirve
    // de referencia. Sólo se muestra en la leyenda (refOnly), con su
    // magnitud absoluta — el polígono sería redundante con el anillo
    // del 100%.
    {
      label: 'Entregado',
      color: '#6B7280',
      refOnly: true,
      formatValue: pct,
      formatRaw: fmt$,
      totalValue: 1,
      totalRaw: totalBuyValue,
      values: topForChart.map(() => 1),
      rawValues: topForChart.map((g) => g.buyValue),
    },
    {
      label: 'Venta campaña',
      color: '#1F6FB2',
      formatValue: pct,
      formatRaw: fmt$,
      totalValue: safeRatio(totalPvfIn, totalBuyValue),
      totalRaw: totalPvfIn,
      values: topForChart.map((g) => safeRatio(g.sellValuePvfInSeason, g.buyValue)),
      rawValues: topForChart.map((g) => g.sellValuePvfInSeason),
    },
    {
      label: 'Venta total',
      color: '#3E8E5A',
      formatValue: pct,
      formatRaw: fmt$,
      totalValue: safeRatio(totalPvfTotal, totalBuyValue),
      totalRaw: totalPvfTotal,
      values: topForChart.map((g) => safeRatio(g.sellValuePvfTotal, g.buyValue)),
      rawValues: topForChart.map((g) => g.sellValuePvfTotal),
    },
    {
      label: 'Almacén',
      color: '#D17B3F',
      formatValue: pct,
      formatRaw: fmt$,
      totalValue: safeRatio(totalRemainCI, totalBuyValue),
      totalRaw: totalRemainCI,
      values: topForChart.map((g) => safeRatio(g.remainValueCI, g.buyValue)),
      rawValues: topForChart.map((g) => g.remainValueCI),
    },
  ];

  // UNIDADES: mismo enfoque, sobre uds compradas.
  const unitsSeries = [
    {
      label: 'Entregado',
      color: '#6B7280',
      refOnly: true,
      formatValue: pct,
      formatRaw: fmtU,
      totalValue: 1,
      totalRaw: totalBuyUnits,
      values: topForChart.map(() => 1),
      rawValues: topForChart.map((g) => g.buyUnits),
    },
    {
      label: 'Vendidas campaña',
      color: '#1F6FB2',
      formatValue: pct,
      formatRaw: fmtU,
      totalValue: safeRatio(totalSellUnitsIn, totalBuyUnits),
      totalRaw: totalSellUnitsIn,
      values: topForChart.map((g) => safeRatio(g.sellUnitsInSeason, g.buyUnits)),
      rawValues: topForChart.map((g) => g.sellUnitsInSeason),
    },
    {
      label: 'Vendidas total',
      color: '#3E8E5A',
      formatValue: pct,
      formatRaw: fmtU,
      totalValue: safeRatio(totalSellUnitsTotal, totalBuyUnits),
      totalRaw: totalSellUnitsTotal,
      values: topForChart.map((g) => safeRatio(g.sellUnitsTotal, g.buyUnits)),
      rawValues: topForChart.map((g) => g.sellUnitsTotal),
    },
    {
      label: 'Restantes',
      color: '#D17B3F',
      formatValue: pct,
      formatRaw: fmtU,
      totalValue: safeRatio(totalRemainUnits, totalBuyUnits),
      totalRaw: totalRemainUnits,
      values: topForChart.map((g) => safeRatio(g.remainUnits, g.buyUnits)),
      rawValues: topForChart.map((g) => g.remainUnits),
    },
  ];

  const isValueMode = chartMode === 'value';
  const series = isValueMode ? valueSeries : unitsSeries;
  const helpText = isValueMode
    ? `Cada serie como % de la compra de cada grupo (${topForChart.length} ${topForChart.length === 1 ? 'grupo' : 'grupos'}). Escala fija 0–100%.`
    : `Cada serie como % de las uds compradas de cada grupo (${topForChart.length} ${topForChart.length === 1 ? 'grupo' : 'grupos'}). Escala fija 0–100%.`;

  // Narrativa automática de rentabilidad. Mira la rentab. de cada
  // grupo (PVF total / compra − 1) y compone un texto-resumen con los
  // 3 mejores y peores. Edge cases: ninguna rentable, todas rentables,
  // sin datos suficientes.
  const groupNameOf = (g) =>
    !isEmptyDim(g.subcategory)
      ? g.subcategory
      : !isEmptyDim(g.category)
        ? g.category
        : !isEmptyDim(g.gender)
          ? g.gender
          : '—';
  const rentabilidades = chartGroups
    .filter((g) => g.buyValue > 0)
    .map((g) => ({ label: groupNameOf(g), value: (g.sellValuePvfTotal - g.buyValue) / g.buyValue }))
    // Si todas las dims del grupo son vacías el label queda en "—" —
    // no es accionable, lo dejamos fuera del análisis de rentabilidad.
    .filter((r) => r.label !== '—');
  // Mejores: rentables ordenadas de MÁS a MENOS (top 3).
  // Por debajo: en pérdida ordenadas de MENOS a MÁS (=peores primero,
  // top 3). Las dos listas se calculan sobre conjuntos independientes
  // para que no haya solapamientos.
  const profitable = rentabilidades.filter((r) => r.value >= 0).sort((a, b) => b.value - a.value);
  const losing = rentabilidades.filter((r) => r.value < 0).sort((a, b) => a.value - b.value);
  const top3 = profitable.slice(0, 3);
  const bottom3 = losing.slice(0, 3);

  let summaryLine;
  if (rentabilidades.length === 0) {
    summaryLine = 'Sin datos de compra para evaluar rentabilidad.';
  } else if (losing.length === 0) {
    summaryLine = `Todas las ${rentabilidades.length} categoría${rentabilidades.length === 1 ? '' : 's'} son rentables.`;
  } else if (profitable.length === 0) {
    summaryLine = `Ninguna de las ${rentabilidades.length} categoría${rentabilidades.length === 1 ? '' : 's'} es rentable.`;
  } else {
    summaryLine = `${rentabilidades.length} grupos · ${profitable.length} rentable${profitable.length === 1 ? '' : 's'} · ${losing.length} en pérdidas.`;
  }

  const listRow = (r, sign) => (
    <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
      <span style={{ color: '#374151' }}>• {r.label}</span>
      <strong style={{ color: sign === 'pos' ? '#0F7B3E' : sign === 'neg' ? '#C0392B' : '#000' }}>
        {(r.value >= 0 ? '+' : '') + (r.value * 100).toFixed(1) + '%'}
      </strong>
    </div>
  );

  // Mode tabs en línea con la columna de la leyenda. Es un control
  // visual propio del chart (no afecta a la tabla), por eso vive aquí
  // y no en la barra superior. Estilo tab-underline: minimalista, no
  // pesa visualmente sobre el resto del panel.
  const modeTabs = [
    { id: 'value', label: 'Valor económico' },
    { id: 'units', label: 'Unidades' },
  ];
  const modeSwitcher = (
    <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #E1E3E5', marginBottom: 14 }}>
      {modeTabs.map((tab) => {
        const active = chartMode === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => setChartMode(tab.id)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 12px',
              fontSize: 12,
              fontWeight: active ? 700 : 500,
              color: active ? '#1F3A5F' : '#6B7280',
              borderBottom: active ? '2px solid #1F3A5F' : '2px solid transparent',
              marginBottom: -1,
              userSelect: 'none',
              transition: 'color 0.15s ease, border-color 0.15s ease',
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
  // Cabecera del panel de la leyenda: sólo el selector de modo. El
  // título de campaña (Verano/Invierno YYYY) vive en el título de la
  // Card, no en la leyenda — así no compite con el title del chart.
  const legendHeader = modeSwitcher;

  // Narrativa de rentabilidad — mismo estilo de título que "Totales"
  // (font 13, bold, tinta azul, borde inferior) para que ambos bloques
  // se sientan parte del mismo panel.
  const narrative = (
    <div style={{ fontSize: 12 }}>
      <div
        style={{
          fontWeight: 700,
          color: '#1F3A5F',
          marginTop: 20,
          marginBottom: 8,
          fontSize: 13,
          borderBottom: '1px solid #E1E3E5',
          paddingBottom: 6,
        }}
      >
        Rentabilidad
      </div>
      <div style={{ color: '#6B7280', marginBottom: 6 }}>{summaryLine}</div>
      {top3.length > 0 && (
        <>
          <div style={{ color: '#6B7280', marginTop: 6 }}>Mejores:</div>
          {top3.map((r) => listRow(r, 'pos'))}
        </>
      )}
      {bottom3.length > 0 && (
        <>
          <div style={{ color: '#6B7280', marginTop: 6 }}>Por debajo:</div>
          {bottom3.map((r) => listRow(r, 'neg'))}
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Tabla en su propia Card con entradilla. La toggle de
          subcategorías ahora es global (vive arriba al lado de los
          Tabs en ViewReportEfficacy), por eso `topControls` ya no se
          pasa aquí. */}
      <Card>
        <Box padding="4">
          {/* Título dinámico: "Métricas por género · categoría · subcategoría"
              construido con las dimensiones activas. Antes vivía partido
              entre dos textos — el outer del Card y el inner de la
              tabla. Lo fusionamos en uno solo aquí arriba. */}
          <Text variant="headingMd" as="h3">
            {`Métricas por ${dimensions.map((d) => DIM_LABEL[d].toLowerCase()).join(' · ')}` ||
              'Métricas (total agregado)'}
          </Text>
          <Box minHeight=".25rem" />
          <Text variant="bodySm" tone="subdued">
            Eficacia, rentabilidad y dto. se recalculan sobre los sumatorios.
            <br />
            Click en cualquier cabecera para ordenar.
          </Text>
          <VerticalSpace />
          <BreakdownByCategory rows={rows} dimensions={dimensions} bare />
        </Box>
      </Card>
      <Box minHeight=".5rem" />
      {/* Comparativa visual: radar con varias capas (compra, venta
          campaña, venta total, almacén) por categoría. Toggle de
          modo (Valor / Uds) vive dentro de la leyenda del chart. */}
      <Card>
        <Box padding="4">
          <Text variant="headingMd" as="h3">
            Comparativa visual: {isValueMode ? 'Valores económicos' : 'Unidades'}
            {campaignLabel && <span style={{ color: '#5C5F62', fontWeight: 500 }}>{` · ${campaignLabel}`}</span>}
          </Text>
          <Box minHeight=".25rem" />
          <Text variant="bodySm" tone="subdued">
            Radar de métricas por taxonomías. Muestra ventas en temporada, totales y almacén. <br />
            Cambia entre valor económico y unidades en la leyenda del gráfico.
            <br /> {helpText}
          </Text>
          <VerticalSpace />
          <RadarChart
            axes={axes}
            axisGroups={axisGroups}
            series={series}
            legendHeader={legendHeader}
            legendFooter={narrative}
          />
        </Box>
      </Card>
    </>
  );
};
